import type { FieldCovariate } from "../types/contract";
import { BUNDLED_REFERENCE } from "./referenceData";
import type { WeightMap } from "./zoneEdit";

// Relative-zone design PRESETS: one-click layer selections + weights for the "Multi-Input Relative
// Zones" customize page.

const withValues = (catalog: FieldCovariate[]) =>
  catalog.filter((c) => c.has_values);
function firstKey(
  catalog: FieldCovariate[],
  category: string,
  re: RegExp,
  yearDesc = false,
): string | undefined {
  let rows = withValues(catalog).filter(
    (c) => c.category === category && re.test(c.key),
  );
  if (yearDesc) rows = [...rows].sort((a, b) => (b.year ?? 0) - (a.year ?? 0));
  return rows[0]?.key;
}
const ssurgoMapUnit = (c: FieldCovariate[]) =>
  firstKey(c, "ssurgo", /mapunit/i); // SSURGO map unit name (categorical)
const twi = (c: FieldCovariate[]) => firstKey(c, "topography", /twi/i); // topographic wetness index
// No broad-NDVI fallback: a field without an ndvi_peak layer renormalizes to soil+TWI rather than
// silently picking the layer this matcher exists to avoid.
const yieldOrNdvi = (c: FieldCovariate[]) =>
  firstKey(c, "machine", /yield/i, true) ??
  firstKey(c, "satellite", /(?<![a-z])ndvi_?peak/i, true);

// build a normalized {checked, weights} from [key, weight] pairs: drop missing keys, renormalize the rest to 100
function build(
  pairs: [string | undefined, number][],
): { checked: string[]; weights: WeightMap } | null {
  const present = pairs.filter(
    (p): p is [string, number] => !!p[0] && p[1] > 0,
  );
  if (!present.length) return null;
  const total = present.reduce((s, [, w]) => s + w, 0);
  const checked = present.map(([k]) => k);
  const weights: WeightMap = {};
  present.forEach(([k, w]) => {
    weights[k] = Math.round((w / total) * 1000) / 10;
  });
  // absorb rounding drift into the first (highest-weighted) layer so the sum is exactly 100
  const drift = 100 - checked.reduce((s, k) => s + weights[k], 0);
  weights[checked[0]] = Math.round((weights[checked[0]] + drift) * 10) / 10;
  return { checked, weights };
}

export interface ZonePreset {
  id: string;
  label: string;
  hint: string;
  resolve: (
    catalog: FieldCovariate[],
  ) => { checked: string[]; weights: WeightMap } | null;
}

type PresetRole = "ssurgo_mapunit" | "twi" | "yield_or_ndvi";

const ROLE: Record<
  PresetRole,
  (catalog: FieldCovariate[]) => string | undefined
> = {
  ssurgo_mapunit: ssurgoMapUnit,
  twi,
  yield_or_ndvi: yieldOrNdvi,
};

export const ZONE_PRESETS: ZonePreset[] = [];

export interface ZonePresetDocument {
  presets: {
    id: string;
    label: string;
    hint: string;
    layers: { role: PresetRole; weight: number }[];
  }[];
}

export function installZonePresets(doc: ZonePresetDocument): void {
  const built = doc.presets.map((preset) => ({
    id: preset.id,
    label: preset.label,
    hint: preset.hint,
    resolve: (catalog: FieldCovariate[]) =>
      build(
        preset.layers.map((layer) => [ROLE[layer.role](catalog), layer.weight]),
      ),
  }));
  ZONE_PRESETS.splice(0, ZONE_PRESETS.length, ...built);
}

installZonePresets(BUNDLED_REFERENCE["zone-presets"] as ZonePresetDocument);

export const DEFAULT_ZONE_PRESET: ZonePreset = {
  get id() {
    return ZONE_PRESETS[0]?.id ?? "";
  },
  get label() {
    return ZONE_PRESETS[0]?.label ?? "";
  },
  get hint() {
    return ZONE_PRESETS[0]?.hint ?? "";
  },
  resolve: (catalog) => ZONE_PRESETS[0]?.resolve(catalog) ?? null,
};
